import { useEffect, useRef } from "react"

interface MeshGradientProps {
  className?: string
}

export default function MeshGradient({ className = "" }: MeshGradientProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches
    if (prefersReducedMotion) return

    const container = containerRef.current
    if (!container) return

    const orbs = container.querySelectorAll<HTMLDivElement>(".mesh-orb")
    let animFrameId: number

    const animate = (time: number) => {
      orbs.forEach((orb, i) => {
        const speed = 0.0003 + i * 0.0001
        const x = Math.sin(time * speed + i * 2) * 15
        const y = Math.cos(time * speed * 0.7 + i * 1.5) * 10
        orb.style.transform = `translate(${x}%, ${y}%)`
      })
      animFrameId = requestAnimationFrame(animate)
    }

    animFrameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animFrameId)
  }, [])

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
      aria-hidden="true"
    >
      {/* Violet orb */}
      <div
        className="mesh-orb absolute top-[-20%] left-[-10%] w-[60vw] h-[60vw] rounded-full opacity-20"
        style={{
          background:
            "radial-gradient(circle, rgba(124,58,237,0.8) 0%, transparent 70%)",
          filter: "blur(80px)",
        }}
      />
      {/* Cyan orb */}
      <div
        className="mesh-orb absolute top-[20%] right-[-15%] w-[50vw] h-[50vw] rounded-full opacity-15"
        style={{
          background:
            "radial-gradient(circle, rgba(6,182,212,0.7) 0%, transparent 70%)",
          filter: "blur(80px)",
        }}
      />
      {/* Dark purple orb */}
      <div
        className="mesh-orb absolute bottom-[-10%] left-[20%] w-[45vw] h-[45vw] rounded-full opacity-20"
        style={{
          background:
            "radial-gradient(circle, rgba(88,28,135,0.9) 0%, transparent 70%)",
          filter: "blur(60px)",
        }}
      />
      {/* Subtle overlay grid */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />
    </div>
  )
}
